import React, { useEffect, useId, useState } from 'react';
import api from '@/api';

const EMPTY = { name: '', address: '', phone: '', email: '', country: '' };
const COUNTRY_SUGGESTIONS = ['DK', 'SE', 'LT', 'LV', 'EE', 'FI', 'NO', 'GB', 'DE', 'PL'];

/**
 * Add or (with `host`, admins only - the API checks) edit a host. Only name and country are
 * required. onSaved(host) gets the saved host; errors from the API (e.g. an email that another host
 * has) are shown in the dialog.
 */
export default function HostModal({ open, onClose, onSaved, host = null }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const id = useId();

  useEffect(() => {
    if (!open) return;
    setForm(host ? { ...EMPTY, ...host } : EMPTY);
    setError('');
  }, [host, open]);

  // Dismiss on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const field = (key, label, props = {}) => (
    <div>
      <label htmlFor={`${id}-${key}`} className="block font-medium">{label}</label>
      <input
        id={`${id}-${key}`}
        className="w-full rounded border px-3 py-2 dark:bg-gray-700 dark:border-gray-600"
        value={form[key] ?? ''}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        {...props}
      />
    </div>
  );

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    const blankToNull = (v) => (typeof v === 'string' && v.trim() === '' ? null : v);
    try {
      const res = await api.post('/masterdata/hosts/store', {
        id: host?.id,
        name: form.name.trim(),
        address: blankToNull(form.address),
        phone: blankToNull(form.phone),
        email: blankToNull(form.email),
        country: form.country.trim().toUpperCase(),
      });
      onSaved?.(res?.data?.host ?? null);
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 422 || status === 403
        ? err.response.data?.message || 'Could not save the host.'
        : 'Could not save the host. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-label={host ? 'Edit host' : 'Add host'} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-lg relative" onMouseDown={(e) => e.stopPropagation()}>
        <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700" onClick={onClose} aria-label="Close">&times;</button>
        <h2 className="text-lg font-bold mb-4">{host ? 'Edit Host' : 'Add Host'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {field('name', 'Name *', { required: true, autoFocus: true })}
          {field('address', 'Address')}
          {field('phone', 'Phone')}
          {field('email', 'Email', { type: 'email' })}
          {field('country', 'Country *', { required: true, placeholder: 'e.g. DK', list: `${id}-countries`, maxLength: 10 })}
          <datalist id={`${id}-countries`}>
            {COUNTRY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
          </datalist>
          {error && <div role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-emerald-700 text-white font-semibold hover:bg-emerald-800 disabled:opacity-60">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
